'use-client';
import React, { useCallback, useImperativeHandle } from 'react';
import {
  formatClassNames,
  getDataAttributes,
} from '@wix/editor-elements-common-utils';
import type {
  ISignatureInputImperativeActions,
  ISignatureInputProps,
} from '../../../SignatureInput.types';
import semanticClassNames from '../../../SignatureInput.semanticClassNames';
import { st, classes } from '../../style/TypeOrDrawSignatureInput.st.css';
import { SignatureInputRadioGroup } from './SignatureInputRadioGroup';
import { useSignatureInput } from './useSignatureInput';
import { DataHooks } from './constants';

const TypeOrDrawSignatureInput: React.ForwardRefRenderFunction<
  ISignatureInputImperativeActions,
  ISignatureInputProps
> = (props, ref) => {
  const {
    required,
    shouldShowValidityIndication,
    isValid,
    isDisabled,
    id,
    label,
    clearButtonText,
    customClassNames,
    className,
    onMouseEnter,
    onMouseLeave,
    translations,
  } = props;

  const {
    padContainerRef,
    inputRef,
    canvasRef,
    selectedMode,
    isDrawing,
    isFocused,
    directionRef,
    a11yValue,
    focusTypeMode,
    blurTypeMode,
    handleRadioChange,
    handleClear,
    handleKeyDown,
    _onFocus,
    _onBlur,
    handleTypedSignatureChange,
  } = useSignatureInput(props);

  const onFocusCanvas = useCallback(
    (a: React.FocusEvent) => {
      _onFocus(a);
      if (selectedMode === 'type') {
        inputRef.current?.focus();
      }
    },
    [_onFocus, selectedMode, inputRef],
  );

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (selectedMode === 'type') {
        focusTypeMode();
      } else {
        canvasRef.current?.focus();
      }
    },
    blur: () => {
      if (selectedMode === 'type') {
        blurTypeMode();
      } else {
        canvasRef.current?.blur();
      }
    },
    clear: () => {
      handleClear({ shouldTriggerChangeEvent: false });
    },
    setCustomValidity: message => {
      if (message.type === 'message') {
        inputRef.current?.setCustomValidity(message.message);
      }
    },
  }));

  const handleClearButtonClick = useCallback(() => {
    handleClear({ shouldTriggerChangeEvent: true });
  }, [handleClear]);

  const inputId = `input_${id}`;

  const rootClassName = st(
    classes.root,
    {
      required,
      disabled: isDisabled,
      error: !isValid && shouldShowValidityIndication,
      focus: !isDisabled && (isDrawing || isFocused),
    },
    className,
    formatClassNames(semanticClassNames.root, ...(customClassNames || [])),
  );

  const typeMode = selectedMode === 'type';

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      className={rootClassName}
      ref={padContainerRef}
      {...(!isDisabled && {
        onMouseEnter,
        onMouseLeave,
      })}
    >
      <SignatureInputRadioGroup
        id={id}
        disabled={isDisabled}
        selectedMode={selectedMode}
        onChange={handleRadioChange}
        translations={translations}
        label={label}
      />
      <div
        className={classes.padContainer}
        onKeyDown={handleKeyDown}
        ref={directionRef}
        role="region"
      >
        <canvas
          ref={canvasRef}
          className={st(classes.pad, typeMode ? classes.hiddenCanvas : '')}
          aria-hidden="true"
          tabIndex={typeMode ? -1 : 0}
          role="img"
          onBlur={_onBlur}
          onFocus={onFocusCanvas}
          data-hook={DataHooks.canvas}
        />
        <input
          id={inputId}
          className={st(
            classes.textInput,
            typeMode ? '' : classes.visuallyHidden,
          )}
          ref={inputRef}
          value={a11yValue}
          onChange={handleTypedSignatureChange}
          disabled={isDisabled || selectedMode !== 'type'}
          aria-required={required}
          aria-invalid={!isValid}
          tabIndex={typeMode ? 0 : -1}
          data-hook={DataHooks.input}
          type="text"
        />
      </div>
      <div className={classes.buttonContainer}>
        <button
          className={st(
            classes.clearButton,
            'has-custom-focus',
            formatClassNames(semanticClassNames.button),
          )}
          disabled={isDisabled}
          aria-controls={inputId}
          aria-label={`${clearButtonText} ${label}`}
          onClick={handleClearButtonClick}
          data-hook={DataHooks.clearButton}
        >
          {clearButtonText}
        </button>
      </div>
    </div>
  );
};

export default React.forwardRef(TypeOrDrawSignatureInput);
